import React, { useEffect, useState, useCallback } from "react";
import { collection, query, orderBy, onSnapshot, doc, getDoc, deleteDoc, addDoc, updateDoc, limit, Timestamp } from "firebase/firestore";
import { auth, db } from "../components/firebase";
import "../styles/pantryItemsOperations.css";

const ITEMS_PER_PAGE = 15;
const CATEGORIES = ["Fruits", "Vegetables", "Dairy", "Meat", "Grain / Grain Products", "Snacks", "Breakfast", "Spices", "Condiments", "Sauces", "Canned Food", "Other"];

const PantryItemOperations = () => {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [expiryDate, setExpiryDate] = useState("");
  const [category, setCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [updateItemId, setUpdateItemId] = useState(null);

  // Filter and paginate items based on searchTerm and currentPage
  const updatePaginatedItems = useCallback(
    (itemsList) => {
      const filteredItems = itemsList.filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase()));

      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;

      setItems(filteredItems.slice(startIndex, endIndex));
      setTotalPages(Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE)));
    },
    [searchTerm, currentPage]
  );

  // Subscribe to Firestore pantry items
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const itemsRef = collection(db, "PantryList", user.uid, "pantry");
    const q = query(itemsRef, orderBy("createdAt", "desc"), limit(ITEMS_PER_PAGE * 10));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const itemList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllItems(itemList);
      updatePaginatedItems(itemList);
    });

    return () => unsubscribe();
  }, [updatePaginatedItems]);

  // Re-filter and paginate when searchTerm, currentPage, or allItems change
  useEffect(() => {
    if (allItems.length) {
      updatePaginatedItems(allItems);
    }
  }, [searchTerm, currentPage, allItems, updatePaginatedItems]);

  // Add new item or update existing item in Firestore
  const handleAddOrUpdate = async (e) => {
    e.preventDefault();
    const user = auth.currentUser;
    if (!user) return;

    if (!name.trim()) {
      alert("Item name cannot be empty");
      return;
    }

    try {
      const pantryRef = collection(db, "PantryList", user.uid, "pantry");

      if (updateItemId) {
        // 🔍 Fetch the existing item to preserve `createdAt`
        const itemDocRef = doc(pantryRef, updateItemId);
        const itemSnap = await getDoc(itemDocRef);

        if (!itemSnap.exists()) {
          alert("Item not found for update");
          return;
        }

        const existingItem = itemSnap.data();

        const updatedData = {
          name: name.trim(),
          quantity: Number(quantity),
          expiryDate: expiryDate ? Timestamp.fromDate(new Date(expiryDate)) : null,
          category,
          createdAt: existingItem.createdAt || Timestamp.now(), // 🛠️ Preserve createdAt
        };

        await updateDoc(itemDocRef, updatedData);
        alert("Item updated successfully!");
      } else {
        // ✅ Add new item
        const newItem = {
          name: name.trim(),
          quantity: Number(quantity),
          createdAt: Timestamp.now(),
          expiryDate: expiryDate ? Timestamp.fromDate(new Date(expiryDate)) : null,
          category,
        };

        await addDoc(pantryRef, newItem);
        alert("Item added successfully!");
      }

      // 🔄 Reset form
      setName("");
      setQuantity(1);
      setExpiryDate("");
      setCategory("");
      setUpdateItemId(null);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error adding/updating item: ", error);
      alert("Failed to add/update item, please try again.");
    }
  };

  // Delete item from Firestore
  const handleDelete = async (itemId) => {
    const user = auth.currentUser;
    if (!user) return;

    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      await deleteDoc(doc(db, "PantryList", user.uid, "pantry", itemId));
    } catch (error) {
      console.error("Error deleting item: ", error);
      alert("Failed to delete item, please try again.");
    }
  };

  // Prepare form for updating an item
  const handleUpdateClick = (item) => {
    setName(item.name);
    setQuantity(item.quantity);
    setExpiryDate(item.expiryDate ? new Date(item.expiryDate.toDate()).toISOString().split("T")[0] : "");
    setCategory(item.category || "");
    setUpdateItemId(item.id);
  };

  // Handle page change
  const handlePagination = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="pantry-container">
      <h1>Pantry List</h1>

      <form onSubmit={handleAddOrUpdate} className="add-item-form">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Item Name" required />
        <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="Quantity" min="1" required />
        <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} placeholder="Expiry Date (optional)" />
        <select value={category} onChange={(e) => setCategory(e.target.value)} required>
          <option value="">Select Category</option>
          {CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <button className="btn btn-outline-primary" type="submit">
          {updateItemId ? "Update Item" : "Add Item"}
        </button>
      </form>

      <input
        type="text"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setCurrentPage(1); // reset page on search change
        }}
        placeholder="Search items"
        className="search-bar"
      />

      <table className="table table-hover">
        <thead>
          <tr>
            <th>Item Name</th>
            <th>Quantity</th>
            <th>Expiry Date</th>
            <th>Category</th>
            <th>Actions</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.length ? (
            items.map((item) => (
              <tr key={item.id}>
                <td>{item.name}</td>
                <td>{item.quantity}</td>
                <td>{item.expiryDate ? new Date(item.expiryDate.toDate()).toLocaleDateString() : "---"}</td>
                <td>{item.category || "---"}</td>
                <td>
                  <button className="btn btn-outline-primary" onClick={() => handleUpdateClick(item)} type="button">
                    Update
                  </button>
                </td>
                <td>
                  <button className="btn btn-outline-danger" onClick={() => handleDelete(item.id)} type="button">
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" style={{ textAlign: "center" }}>
                No items found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="pagination">
        <button onClick={() => handlePagination(currentPage - 1)} disabled={currentPage === 1} className="pagination-btn">
          &laquo; Prev
        </button>
        {Array.from({ length: totalPages }, (_, index) => (
          <button key={index} onClick={() => handlePagination(index + 1)} className={index + 1 === currentPage ? "active" : ""}>
            {index + 1}
          </button>
        ))}
        <button onClick={() => handlePagination(currentPage + 1)} disabled={currentPage === totalPages} className="pagination-btn">
          Next &raquo;
        </button>
      </div>
    </div>
  );
};

export default PantryItemOperations;
